// ============================================================================
// ServiceLineCard — the ONE editor for a single service line (2026-09-08).
//
// It was inside sop-create-modal.tsx, where only the create form could reach
// it, and the account card could therefore only ever RENDER lines. That is the
// whole defect this file exists to close: a card created with one line could
// never gain a second, so an AM who needed a day porter alongside a nightly
// crew opened a second onboarding card for the same hospital.
//
// ⛔ There is ONE line editor. The create form and the account card both mount
// this component. Do not build a second one — a narrower variant is a prop on
// this component (the way `rateLocked` is), never a copy.
//
// TWO THINGS CHANGED WHEN IT MOVED, both deliberate:
//
//   1. THE FIRST LINE IS NOT SPECIAL ANY MORE. It used to hide its own client
//      price and borrow the "Client Monthly Price" header field, so line 1 and
//      the header were the same number typed in one place and lines 2+ were
//      typed somewhere else. The header is now the SUM of the lines and every
//      line — including the first — owns its own price. One rule, no special
//      case, and the repeater reads as a repeater.
//
//   2. A LIVE LINE'S RATES GO THROUGH THE ONE DOOR. `rateLocked` makes the
//      money boxes read-only and `onChangeRate` puts a button beside them that
//      opens the one rate-change process (TCSD-0188) — which records the
//      change, applies it on the date it is given, and tells Finance, the
//      National Account Manager and the executives. ⛔ This component never
//      writes a rate on a live line; an editor that did would be a second door,
//      and a second door is where a change goes missing.
// ============================================================================

import { Trash2 } from 'lucide-react';
import { SmartSelect, isAssignableSub } from '../../components/smart-select';
import { Field, INPUT_CLS } from './intake-parts';
import {
  NAS_BILLING_METHOD_OPTIONS,
  lineMonthlySub,
  type ServiceLineForm,
  type NasBillingMethod,
  type NasSubRateType,
} from './lib/service-lines';
import type { SubMode } from './lib/sub-assignment';
import { RecurringServiceSelect } from '../project-work/service-type-select';

const SUB_MODES: SubMode[] = ['previous', 'new', 'unassigned'];

// ============================================================================
// MarginEstimator — the AMs' calculator, moved off the card and onto the line.
//
// It used to sit once on the Contractor tab and fill the card's single sub-pay
// box. That box paid nobody, and "the sub payment" was never one number anyway:
// Houston Physicians runs a nightly crew at $13,650 and a day porter at $3,163
// on the same building. A calculator that can only fill the first of those is
// wrong there on the day it ships, and wrong quietly.
//
// ⛔ THE MARGIN IS NOT STORED. It is the client rate and the sub rate, which
// the line already carries -- (client - sub) / client. Storing it would be a
// third copy that drifts the moment either rate changes, which is the whole
// family of defect this run exists to close. Dragging sets the sub pay; the
// handle's position is read back out of the two rates every render.
//
// ⛔ AND IT IS LOCKED WHEN THE MONEY IS. On a building already being cleaned
// the sub's pay moves through the rate-change process, so the slider is
// disabled for exactly the same reason the box beneath it is.
// ============================================================================
function MarginEstimator({
  line,
  onField,
  disabled,
}: {
  line: ServiceLineForm;
  onField: <K extends keyof ServiceLineForm>(key: K, value: ServiceLineForm[K]) => void;
  disabled: boolean;
}) {
  const client = Number(line.client_rate);
  const sub = Number(line.sub_rate_monthly);
  const haveClient = line.client_rate.trim() !== '' && Number.isFinite(client) && client > 0;
  const haveSub = line.sub_rate_monthly.trim() !== '' && Number.isFinite(sub) && sub >= 0;

  // No client rate, nothing to take a margin OF. Say so rather than show a
  // slider that cannot move.
  if (!haveClient) {
    return (
      <p className="mt-1 text-[11px] text-tcs-text-secondary">
        Set the client rate above and a margin estimator appears here.
      </p>
    );
  }

  const pct = haveSub ? Math.round(((client - sub) / client) * 100) : 0;
  const shown = Math.min(60, Math.max(0, pct));

  return (
    <div className="mt-2">
      <div className="flex items-center justify-between text-[11px]">
        <span className="font-medium text-tcs-text">Target margin</span>
        <span className="font-mono text-tcs-text">{haveSub ? `${pct}%` : '—'}</span>
      </div>
      <input
        type="range"
        min="0"
        max="60"
        step="1"
        value={shown}
        disabled={disabled}
        onChange={(e) =>
          onField(
            'sub_rate_monthly',
            String(Math.round(client * (1 - Number(e.target.value) / 100))),
          )
        }
        className="mt-1 w-full"
        aria-label="Target margin percent for this service line"
      />
      <p className="text-[11px] text-tcs-text-secondary">
        Estimator only — the COO approves the actual payout.
        {pct > 60 && ' Above 60%: the handle stops, the figure above is the real one.'}
      </p>
    </div>
  );
}

export interface ServiceLineCardProps {
  index: number;
  line: ServiceLineForm;
  onField: <K extends keyof ServiceLineForm>(
    key: K,
    value: ServiceLineForm[K],
  ) => void;
  onRemove: () => void;
  canRemove: boolean;
  /** Whole card read-only (a saving card, a cancelled one). */
  disabled?: boolean;
  /**
   * The money inputs only. Set on a building that is already being served:
   * the rate moves through the rate-change process, not through this editor.
   */
  rateLocked?: boolean;
  /** What the reader is told instead. Required reading when rateLocked. */
  rateLockedHint?: string;
  /** "Remove" on a card is literal; on a live building it closes the line out. */
  removeLabel?: string;
  /**
   * Opens the one rate-change process for this line. Present only when the
   * line is live on a building. ⛔ The card never writes the rate itself.
   */
  onChangeRate?: (field: 'billing_rate' | 'sub_rate_monthly' | 'sub_hourly_rate') => void;
}

export function ServiceLineCard({
  index,
  line,
  onField,
  onRemove,
  canRemove,
  disabled = false,
  rateLocked = false,
  rateLockedHint,
  removeLabel = 'Remove',
  onChangeRate,
}: ServiceLineCardProps) {
  const monthly = lineMonthlySub(line);
  const moneyOff = disabled || rateLocked;
  return (
    <div className="grid gap-3 rounded-md border border-tcs-border bg-tcs-bg/40 p-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-tcs-text">
          Service line {index + 1}
        </span>
        {canRemove && (
          <button
            type="button"
            onClick={onRemove}
            disabled={disabled}
            className="inline-flex items-center gap-1 rounded p-1 text-tcs-text-secondary hover:text-rag-red disabled:opacity-50"
            aria-label={`${removeLabel} service line ${index + 1}`}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>

      <Field label="What kind of work">
        {/* ⛔ The same control the building's own form and the project screen
            render. One picker, one escape hatch, one set of words — the two
            drop-downs that used to be here were a hand-written copy of a list
            the database already held. */}
        <RecurringServiceSelect
          value={line.service_code}
          onChange={(v) => onField('service_code', v)}
          scopeText={line.notes}
          onScopeTextChange={(v) => onField('notes', v)}
          selectClassName={INPUT_CLS}
          inputClassName={INPUT_CLS}
        />
      </Field>

      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Billing method">
          <select value={line.billing_method} disabled={disabled} onChange={(e) => onField('billing_method', e.target.value as NasBillingMethod)} className={INPUT_CLS}>
            {NAS_BILLING_METHOD_OPTIONS.map((b) => (
              <option key={b} value={b}>{b.replace(/_/g, ' ')}</option>
            ))}
          </select>
        </Field>
        <Field
          label={line.billing_method === 'Hourly' ? 'Client price ($/hr)' : 'Client price ($/mo)'}
          hint={
            line.billing_method === 'Hourly'
              ? 'What the client pays for each hour worked on THIS line. There is no monthly price.'
              : 'What the client pays for THIS line.'
          }
        >
          <input type="number" inputMode="decimal" step="0.01" min="0" value={line.client_rate} disabled={moneyOff} onChange={(e) => onField('client_rate', e.target.value)} className={INPUT_CLS} placeholder="0.00" />
          {onChangeRate && (
            <button
              type="button"
              onClick={() => onChangeRate('billing_rate')}
              disabled={disabled}
              className="mt-1 text-xs font-medium text-tcs-text underline underline-offset-2 disabled:opacity-50"
            >
              Change the client price
            </button>
          )}
        </Field>
      </div>

      {/* Sub selector (3-way, AFB-0026 semantics, per line). */}
      <Field label="Subcontractor">
        <div className="flex flex-wrap gap-1.5">
          {SUB_MODES.map((m) => (
            <button
              key={m}
              type="button"
              disabled={disabled}
              onClick={() => onField('sub_mode', m)}
              className={`rounded-md border px-3 py-1.5 text-sm disabled:opacity-50 ${line.sub_mode === m ? 'border-tcs-inverse bg-tcs-inverse text-tcs-inverse-ink' : 'border-tcs-border bg-tcs-raised text-tcs-text hover:bg-tcs-bg-muted'}`}
            >
              {m === 'previous' ? 'Previous sub' : m === 'new' ? 'New sub' : 'Unassigned'}
            </button>
          ))}
        </div>
      </Field>

      {line.sub_mode === 'previous' && (
        <Field label="Choose existing sub" hint="Search the full active-sub list. Pick one to link it.">
          <SmartSelect entity="service_provider" value={line.service_provider_id || null} onChange={(id) => onField('service_provider_id', id ?? '')} entityLabel="sub" placeholder="Search subs…" disabled={disabled} isSelectable={isAssignableSub} />
        </Field>
      )}

      {line.sub_mode === 'new' && (
        <div className="grid gap-3 rounded-md border border-tcs-border bg-tcs-raised p-3">
          <p className="text-xs text-tcs-text-secondary">Saving creates the sub (Pending) + an onboarding record for HR to approve.</p>
          <Field label="Subcontractor name (go-by)" required><input type="text" value={line.contractor_name} disabled={disabled} onChange={(e) => onField('contractor_name', e.target.value)} className={INPUT_CLS} autoComplete="off" /></Field>
          <Field label="Legal business name (appears on contract)" hint="Optional — onboarding collects it if unknown."><input type="text" value={line.contractor_legal_name} disabled={disabled} onChange={(e) => onField('contractor_legal_name', e.target.value)} className={INPUT_CLS} autoComplete="off" /></Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Phone"><input type="tel" value={line.contractor_phone} disabled={disabled} onChange={(e) => onField('contractor_phone', e.target.value)} className={INPUT_CLS} autoComplete="off" /></Field>
            <Field label="Email"><input type="email" value={line.contractor_email} disabled={disabled} onChange={(e) => onField('contractor_email', e.target.value)} className={INPUT_CLS} autoComplete="off" /></Field>
          </div>
        </div>
      )}

      {/* Sub cost — monthly flat OR hourly, mirroring the DB rollup. */}
      <Field label="Contract rate basis">
        <div className="flex flex-wrap gap-1.5">
          {(['monthly', 'hourly'] as NasSubRateType[]).map((t) => (
            <button key={t} type="button" disabled={moneyOff} onClick={() => onField('sub_rate_type', t)} className={`rounded-md border px-3 py-1.5 text-sm capitalize disabled:opacity-50 ${line.sub_rate_type === t ? 'border-tcs-inverse bg-tcs-inverse text-tcs-inverse-ink' : 'border-tcs-border bg-tcs-raised text-tcs-text hover:bg-tcs-bg-muted'}`}>{t}</button>
          ))}
        </div>
      </Field>

      {line.sub_rate_type === 'monthly' ? (
        <Field label="Monthly Sub Payment ($)" hint="Paid to the subcontractor.">
          <input type="number" inputMode="decimal" step="0.01" min="0" value={line.sub_rate_monthly} disabled={moneyOff} onChange={(e) => onField('sub_rate_monthly', e.target.value)} className={INPUT_CLS} placeholder="0.00" />
          <MarginEstimator line={line} onField={onField} disabled={moneyOff} />
          {onChangeRate && (
            <button
              type="button"
              onClick={() => onChangeRate('sub_rate_monthly')}
              disabled={disabled}
              className="mt-1 text-xs font-medium text-tcs-text underline underline-offset-2 disabled:opacity-50"
            >
              Change the sub payment
            </button>
          )}
        </Field>
      ) : (
        <div className="grid gap-3">
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Hourly rate ($)"><input type="number" inputMode="decimal" step="0.01" min="0" value={line.sub_hourly_rate} disabled={moneyOff} onChange={(e) => onField('sub_hourly_rate', e.target.value)} className={INPUT_CLS} placeholder="0.00" /></Field>
            <Field label="Hours / day"><input type="number" inputMode="decimal" step="0.25" min="0" value={line.sub_hours_per_day} disabled={moneyOff} onChange={(e) => onField('sub_hours_per_day', e.target.value)} className={INPUT_CLS} placeholder="8" /></Field>
            <Field label="Days / week"><input type="number" inputMode="numeric" min="1" max="7" value={line.service_days_per_week} disabled={disabled} onChange={(e) => onField('service_days_per_week', e.target.value)} className={INPUT_CLS} placeholder="5" /></Field>
          </div>
          {/* Items 831/758: the monthly figure on an hourly line is only what
              its set hours come to. The database keeps it equal to that and
              refuses a typed one; with no set hours there is none. */}
          <p className="text-[11px] text-tcs-text-secondary">
            {monthly != null ? (
              <>
                About{' '}
                <span className="font-mono text-tcs-text">${monthly.toLocaleString()}</span>
                {' '}a month — rate × hrs/day × days/wk × 52 ÷ 12, worked out, not typed.
              </>
            ) : (
              'No monthly figure: this line has no set hours.'
            )}
          </p>
          {onChangeRate && (
            <button
              type="button"
              onClick={() => onChangeRate('sub_hourly_rate')}
              disabled={disabled}
              className="justify-self-start text-xs font-medium text-tcs-text underline underline-offset-2 disabled:opacity-50"
            >
              Change the sub hourly rate
            </button>
          )}
        </div>
      )}

      {rateLocked && rateLockedHint && (
        <p className="rounded-md border border-tcs-border bg-tcs-bg-muted px-3 py-2 text-[11px] text-tcs-text-secondary">
          {rateLockedHint}
        </p>
      )}

      <Field label="Line notes"><input type="text" value={line.notes} disabled={disabled} onChange={(e) => onField('notes', e.target.value)} className={INPUT_CLS} autoComplete="off" placeholder="Optional" /></Field>
    </div>
  );
}
